import React, { useState } from 'react';
import { btcTreasuryData, estimatedBtcPrice, getAvailableTickers } from '../data/btcTreasuryData';
import stockApiService from '../services/stockApi';
import Tooltip from './Tooltip';

// Calculate BTC exposure percentage
const calculateBtcExposure = (company) => {
  if (!company) return 0;
  return Math.round((company.btcValue / company.marketCap) * 100);
};

// Determine BTC market cycle phase
const getBtcCyclePhase = () => {
  // Simple logic based on BTC price - in production this would use more sophisticated analysis
  if (estimatedBtcPrice < 30000) return 'Accumulation Phase';
  if (estimatedBtcPrice < 50000) return 'Early Bull Phase';
  if (estimatedBtcPrice < 80000) return 'Bull Market';
  if (estimatedBtcPrice < 120000) return 'Late Bull Phase';
  return 'Distribution Phase';
};

// Scoring algorithm
const calculateScore = (company) => {
  if (!company) return 0;

  let score = 0;
  const btcExposure = calculateBtcExposure(company);

  // BTC Exposure (0-25 points)
  if (btcExposure >= 80) score += 25;
  else if (btcExposure >= 50) score += 22;
  else if (btcExposure >= 30) score += 18;
  else if (btcExposure >= 15) score += 15;
  else if (btcExposure >= 10) score += 12;
  else if (btcExposure >= 5) score += 8;
  else if (btcExposure >= 2) score += 5;
  else score += 2;

  // NAV Premium/Discount (0-20 points)
  if (company.navPremium < -30) score += 20; // Deep discount
  else if (company.navPremium < -10) score += 17;
  else if (company.navPremium < 0) score += 14;
  else if (company.navPremium < 20) score += 10;
  else if (company.navPremium < 50) score += 5;
  else score += 0; // High premium

  // BTC Holdings Scale (0-15 points)
  if (company.btcHoldings >= 100000) score += 15;
  else if (company.btcHoldings >= 50000) score += 12;
  else if (company.btcHoldings >= 20000) score += 10;
  else if (company.btcHoldings >= 10000) score += 8;
  else if (company.btcHoldings >= 5000) score += 5;
  else score += 2;

  // Profitability (0-15 points)
  if (company.profitability === 'Positive') score += 15;
  else if (company.profitability === 'Improving') score += 10;
  else if (company.profitability === 'Mixed' || company.profitability === 'Cyclical') score += 7;
  else if (company.profitability === 'Transitioning') score += 5;
  else score += 0;

  // Leverage Risk (0-15 points)
  if (company.leverageRatio < 0.5) score += 15;
  else if (company.leverageRatio < 1.0) score += 10;
  else if (company.leverageRatio < 1.5) score += 5;
  else if (company.leverageRatio < 2.0) score += 2;
  else score += 0;

  // Strategy & Leadership (0-10 points)
  if (company.leadership.toLowerCase().includes('saylor') || 
      company.leadership.toLowerCase().includes('dorsey')) score += 10;
  else if (company.leadership.toLowerCase().includes('musk') || 
           company.leadership.toLowerCase().includes('armstrong')) score += 8;
  else if (company.leadership.toLowerCase().includes('btc') || 
           company.leadership.toLowerCase().includes('bitcoin')) score += 6;
  else score += 3;

  return Math.min(score, 100);
};

// Generate verdict based on score and attributes
const generateVerdict = (company, score, btcExposure) => {
  if (!company) return '';

  if (score >= 80) {
    return `Exceptional BTC treasury with ${company.btcHoldings.toLocaleString()} BTC (${btcExposure}% of market cap). ${company.leadership.split('-')[0].trim()} leads aggressive accumulation strategy.`;
  } else if (score >= 65) {
    return `Strong BTC proxy holding ${company.btcHoldings.toLocaleString()} BTC. ${company.navPremium > 0 ? `Trading at ${company.navPremium}% premium` : `Trading at ${Math.abs(company.navPremium)}% discount`} to NAV with ${company.leverageRatio < 1 ? 'conservative' : 'moderate'} leverage.`;
  } else if (score >= 50) {
    return `Decent BTC exposure (${btcExposure}% of market cap) but ${company.profitability === 'Negative' ? 'profitability concerns' : company.leverageRatio > 1.5 ? 'elevated leverage' : 'limited Bitcoin focus'} impact the score.`;
  } else if (score >= 35) {
    return `Limited BTC treasury appeal with only ${btcExposure}% exposure. ${company.btcHoldings < 10000 ? 'Small holdings' : 'High premium'} and ${company.profitability} profitability reduce attractiveness.`;
  } else {
    return `Minimal BTC treasury characteristics. Consider pure Bitcoin or dedicated BTC companies like MSTR for better exposure.`;
  }
};

const BTCTreasuryScorer = () => {
  const [ticker, setTicker] = useState('');
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleScore = async () => {
    const upperTicker = ticker.toUpperCase().trim();
    
    if (!upperTicker) {
      setError('Please enter a stock ticker');
      setReport(null);
      return;
    }

    setIsLoading(true);
    setError('');
    setReport(null);

    try {
      // First try to get company from our curated data
      let company = btcTreasuryData[upperTicker];
      let dataSource = 'curated';

      // If not found, fetch from API
      if (!company) {
        console.log(`Fetching data for ${upperTicker} from external API...`);
        company = await stockApiService.createCompanyProfile(upperTicker);
        dataSource = 'api';
      }

      const btcExposure = calculateBtcExposure(company);
      const score = calculateScore(company);
      const verdict = generateVerdict(company, score, btcExposure);
      const btcCycle = getBtcCyclePhase();

      // Determine debt rating based on leverage ratio
      let debtRating = 'Low';
      if (company.leverageRatio > 2) debtRating = 'High';
      else if (company.leverageRatio > 1) debtRating = 'Moderate';

      setReport({
        ...company,
        ticker: upperTicker,
        score,
        verdict,
        btcExposure,
        btcCycle,
        debtRating,
        dataSource
      });
      setError('');
    } catch (error) {
      console.error('Error fetching company data:', error);
      setError(`Unable to fetch data for ${upperTicker}. Please verify the ticker symbol is correct.`);
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleScore();
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 65) return 'text-blue-400';
    if (score >= 50) return 'text-yellow-400';
    if (score >= 35) return 'text-orange-400';
    return 'text-red-400';
  };

  const getPremiumColor = (premium) => {
    if (premium < 0) return 'text-green-400';
    if (premium < 10) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="w-full p-6 bg-gray-900 text-white rounded-lg shadow-xl">
      <h2 className="text-xl sm:text-2xl font-bold mb-4">Bitcoin Treasury Company Scorer</h2>
      
      <div className="mb-4 p-3 bg-gray-800 rounded text-sm">
        <p className="text-gray-400 mb-2">Score ANY Bitcoin treasury company! Uses curated data for known companies, live data for others.</p>
        <details className="cursor-pointer">
          <summary className="text-blue-400 hover:text-blue-300">View curated companies ({getAvailableTickers().length}) - Others will be fetched live</summary>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-gray-300">
            {getAvailableTickers().map(ticker => (
              <div key={ticker} className="flex items-center gap-2">
                <span className="font-mono text-blue-400">{ticker}</span>
                <span className="text-xs text-gray-500">
                  {btcTreasuryData[ticker].name.substring(0, 20)}{btcTreasuryData[ticker].name.length > 20 ? '...' : ''}
                </span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Try any ticker: AAPL, GOOGL, NVDA, AMD, PLTR, etc. System will estimate Bitcoin treasury potential.
          </p>
        </details>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <input
          type="text"
          value={ticker}
          onChange={(e) => setTicker(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter stock ticker (e.g., MSTR)"
          className="flex-1 px-4 py-2 bg-gray-800 text-white rounded border border-gray-700 focus:outline-none focus:border-blue-500"
        />
        <button
          onClick={handleScore}
          disabled={isLoading}
          className="px-4 sm:px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded transition-colors flex items-center justify-center gap-2 whitespace-nowrap min-w-0"
        >
          {isLoading && (
            <svg className="animate-spin h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          <span className="hidden sm:inline">{isLoading ? 'Analyzing...' : 'Score BTC Treasury Company'}</span>
          <span className="sm:hidden">{isLoading ? 'Analyzing...' : 'Score BTC'}</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded text-red-300">
          {error}
        </div>
      )}

      {report && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <div className="border-b border-gray-700 pb-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold">{report.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-gray-400 text-sm">Ticker: {report.ticker}</p>
                  <span className={`px-2 py-1 text-xs rounded ${
                    report.dataSource === 'curated' 
                      ? 'bg-green-900/50 text-green-300 border border-green-700' 
                      : 'bg-blue-900/50 text-blue-300 border border-blue-700'
                  }`}>
                    {report.dataSource === 'curated' ? 'Verified Data' : 'Live Estimate'}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <Tooltip content="Overall score (0-100) based on: BTC Exposure (25pts), NAV Premium/Discount (20pts), Holdings Scale (15pts), Profitability (15pts), Leverage Risk (15pts), Leadership (10pts)">
                  <div className="cursor-help">
                    <div className={`text-4xl font-bold ${getScoreColor(report.score)}`}>
                      {report.score}
                    </div>
                    <div className="text-sm text-gray-400">/ 100</div>
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">BTC Holdings</span>
                  <Tooltip content="Total amount of Bitcoin held by the company in their treasury. Larger holdings indicate stronger conviction and more direct Bitcoin exposure.">
                    <svg className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </div>
                <span className="font-medium">₿ {report.btcHoldings.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">BTC Exposure</span>
                  <Tooltip content="Percentage of the company's market cap represented by Bitcoin holdings. Higher exposure means Bitcoin price movements have more impact on stock price.">
                    <svg className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </div>
                <span className="font-medium">{report.btcExposure}%</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">NAV Premium/Discount</span>
                  <Tooltip content="How the stock price compares to the value of its Bitcoin holdings. Negative means trading below Bitcoin value (discount), positive means above (premium).">
                    <svg className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </div>
                <span className={`font-medium ${getPremiumColor(report.navPremium)}`}>
                  {report.navPremium > 0 ? '+' : ''}{report.navPremium}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">Profitability</span>
                  <Tooltip content="Current profitability status of the company's core business. Positive profitability reduces risk and provides stability.">
                    <svg className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </div>
                <span className={`font-medium ${
                  report.profitability === 'Positive' ? 'text-green-400' : 
                  report.profitability === 'Improving' ? 'text-blue-400' :
                  report.profitability === 'Mixed' || report.profitability === 'Cyclical' ? 'text-yellow-400' : 
                  report.profitability === 'Transitioning' ? 'text-orange-400' : 'text-red-400'
                }`}>
                  {report.profitability}
                </span>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">% of BTC Supply</span>
                  <Tooltip content="Percentage of total Bitcoin supply (21 million) held by this company. Shows their significance in the Bitcoin ecosystem.">
                    <svg className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </div>
                <span className="font-medium">{report.btcPercentOfSupply}%</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">Leverage Risk</span>
                  <Tooltip content="Debt-to-Equity ratio indicates financial leverage. Higher leverage means more risk but potentially higher returns. Low < 0.5, Moderate 0.5-1.5, High > 1.5">
                    <svg className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </div>
                <span className="font-medium">
                  {report.debtRating} ({report.leverageRatio}x D/E)
                </span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">BTC Market Phase</span>
                  <Tooltip content="Current Bitcoin market cycle phase based on price levels. Accumulation < $30k, Early Bull < $50k, Bull Market < $80k, Late Bull < $120k">
                    <svg className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </div>
                <span className="font-medium">{report.btcCycle}</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1">
                  <span className="text-gray-400">Strategy</span>
                  <Tooltip content="Company's approach to Bitcoin acquisition and holding. Treasury conversion, mining operations, or mixed strategies.">
                    <svg className="w-4 h-4 text-gray-500 hover:text-gray-300 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </div>
                <span className="font-medium text-right text-sm">{report.purchaseStrategy}</span>
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-700">
            <div className="flex items-start space-x-2">
              <span className="text-gray-400 font-medium">Verdict:</span>
              <p className="text-gray-200">{report.verdict}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Scoring Methodology Explanation */}
      <div className="mt-8 p-6 bg-gray-800 rounded-lg border border-gray-700">
        <h3 className="text-xl font-bold text-white mb-4">📊 How Companies Are Scored (0-100 Points)</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div className="space-y-4">
            <div className="border-l-4 border-orange-500 pl-4">
              <h4 className="font-semibold text-orange-400 mb-2">🔸 BTC Exposure (0-25 points)</h4>
              <ul className="text-gray-300 space-y-1">
                <li>• <strong>20-25 pts:</strong> 80%+ of market cap in Bitcoin</li>
                <li>• <strong>15-19 pts:</strong> 50-79% exposure</li>
                <li>• <strong>10-14 pts:</strong> 20-49% exposure</li>
                <li>• <strong>5-9 pts:</strong> 5-19% exposure</li>
                <li>• <strong>0-4 pts:</strong> &lt;5% exposure</li>
              </ul>
            </div>
            
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-blue-400 mb-2">💰 NAV Premium/Discount (0-20 points)</h4>
              <ul className="text-gray-300 space-y-1">
                <li>• <strong>18-20 pts:</strong> Trading at 0-10% discount</li>
                <li>• <strong>15-17 pts:</strong> 10-20% discount</li>
                <li>• <strong>12-14 pts:</strong> 20-30% discount</li>
                <li>• <strong>8-11 pts:</strong> Trading at premium</li>
                <li>• <strong>0-7 pts:</strong> &gt;30% discount</li>
              </ul>
            </div>
            
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-semibold text-green-400 mb-2">📈 BTC Holdings Scale (0-15 points)</h4>
              <ul className="text-gray-300 space-y-1">
                <li>• <strong>13-15 pts:</strong> &gt;100,000 BTC</li>
                <li>• <strong>10-12 pts:</strong> 10,000-100,000 BTC</li>
                <li>• <strong>7-9 pts:</strong> 1,000-10,000 BTC</li>
                <li>• <strong>4-6 pts:</strong> 100-1,000 BTC</li>
                <li>• <strong>0-3 pts:</strong> &lt;100 BTC</li>
              </ul>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="font-semibold text-purple-400 mb-2">💼 Core Business Profitability (0-15 points)</h4>
              <ul className="text-gray-300 space-y-1">
                <li>• <strong>13-15 pts:</strong> Consistently profitable</li>
                <li>• <strong>10-12 pts:</strong> Recently profitable/improving</li>
                <li>• <strong>7-9 pts:</strong> Mixed results or cyclical</li>
                <li>• <strong>4-6 pts:</strong> Currently unprofitable</li>
                <li>• <strong>0-3 pts:</strong> Significant losses</li>
              </ul>
            </div>
            
            <div className="border-l-4 border-red-500 pl-4">
              <h4 className="font-semibold text-red-400 mb-2">⚖️ Leverage Risk (0-15 points)</h4>
              <ul className="text-gray-300 space-y-1">
                <li>• <strong>13-15 pts:</strong> No/minimal debt</li>
                <li>• <strong>10-12 pts:</strong> Conservative debt levels</li>
                <li>• <strong>7-9 pts:</strong> Moderate leverage</li>
                <li>• <strong>4-6 pts:</strong> High leverage</li>
                <li>• <strong>0-3 pts:</strong> Excessive debt risk</li>
              </ul>
            </div>
            
            <div className="border-l-4 border-yellow-500 pl-4">
              <h4 className="font-semibold text-yellow-400 mb-2">🎯 Strategy & Leadership (0-10 points)</h4>
              <ul className="text-gray-300 space-y-1">
                <li>• <strong>9-10 pts:</strong> Proven Bitcoin strategy, strong leadership</li>
                <li>• <strong>7-8 pts:</strong> Clear strategy, experienced team</li>
                <li>• <strong>5-6 pts:</strong> Developing strategy</li>
                <li>• <strong>3-4 pts:</strong> Unclear or changing strategy</li>
                <li>• <strong>0-2 pts:</strong> Poor leadership or strategy</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-gray-700 rounded-lg">
          <h4 className="font-semibold text-white mb-3">🏆 Score Interpretation</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="text-center p-2 bg-green-900/30 rounded border border-green-700">
              <div className="font-bold text-green-400">90-100</div>
              <div className="text-green-300">Excellent</div>
            </div>
            <div className="text-center p-2 bg-blue-900/30 rounded border border-blue-700">
              <div className="font-bold text-blue-400">75-89</div>
              <div className="text-blue-300">Good</div>
            </div>
            <div className="text-center p-2 bg-yellow-900/30 rounded border border-yellow-700">
              <div className="font-bold text-yellow-400">60-74</div>
              <div className="text-yellow-300">Average</div>
            </div>
            <div className="text-center p-2 bg-red-900/30 rounded border border-red-700">
              <div className="font-bold text-red-400">Below 60</div>
              <div className="text-red-300">Poor</div>
            </div>
          </div>
        </div>
        
        <div className="mt-4 text-xs text-gray-400">
          <p><strong>Note:</strong> This scoring system analyzes Bitcoin treasury companies as investment vehicles for Bitcoin exposure. 
          Scores are based on publicly available data and should not be considered investment advice. 
          Always conduct your own research before making investment decisions.</p>
        </div>
      </div>
    </div>
  );
};

export default BTCTreasuryScorer;